import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, getToken, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileDown, Trash2, AlertOctagon, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";

const PRIORITY = {
  info: { label: "Informativo", color: "#52525B", Icon: Info },
  important: { label: "Importante", color: "#D97706", Icon: AlertTriangle },
  urgent: { label: "Urgente", color: "#E63946", Icon: AlertOctagon },
};
const AUDIENCE = {
  all: "Toda la organización",
  drivers: "Conductores",
  staff: "Personal interno",
};

export default function AnnouncementDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [a, setA] = useState(null);

  useEffect(() => {
    api
      .get(`/announcements/${id}`)
      .then((r) => setA(r.data))
      .catch((e) =>
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message)
      );
  }, [id]);

  const remove = async () => {
    if (!window.confirm("¿Eliminar este comunicado?")) return;
    await api.delete(`/announcements/${id}`);
    toast.success("Comunicado eliminado");
    navigate("/announcements");
  };

  if (!a) return <div className="p-12 text-center text-zinc-500">Cargando…</div>;
  const p = PRIORITY[a.priority] || PRIORITY.info;
  const Icon = p.Icon;

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-3xl mx-auto" data-testid="announcement-detail">
      <button
        onClick={() => navigate(-1)}
        className="text-xs uppercase tracking-widest text-zinc-500 hover:text-black flex items-center gap-2 mb-6"
      >
        <ArrowLeft size={14} /> Volver
      </button>

      <div
        className="border-2 border-black bg-white p-6 sm:p-8"
        style={{ borderLeftWidth: 8, borderLeftColor: p.color }}
      >
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-1 border"
            style={{ borderColor: p.color, color: p.color }}
          >
            <Icon size={12} /> {p.label}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">
            Para: {AUDIENCE[a.audience]}
          </span>
        </div>

        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-3">
          {a.title}
        </h1>

        <div className="text-xs text-zinc-500 mb-6">
          {new Date(a.created_at).toLocaleString("es-ES")} ·{" "}
          <span className="font-semibold">{a.created_by_name}</span>
          {a.created_by_department ? ` · ${a.created_by_department}` : ""}
          {a.expires_at ? ` · Vigente hasta ${a.expires_at}` : ""}
        </div>

        <article
          className="text-base leading-relaxed whitespace-pre-wrap"
          data-testid="announcement-body"
        >
          {a.body}
        </article>

        <div className="border-t border-zinc-300 pt-6 mt-8 flex flex-wrap gap-3">
          <Button
            onClick={() =>
              window.open(
                `${API}/announcements/${id}/export.pdf?auth=${encodeURIComponent(getToken())}`,
                "_blank"
              )
            }
            className="rounded-none btn-primary h-11 px-5 text-xs uppercase tracking-widest"
            data-testid="ann-pdf-btn"
          >
            <FileDown size={14} className="mr-2" /> Descargar PDF
          </Button>
          {user?.role === "admin" && (
            <Button
              variant="outline"
              onClick={remove}
              className="rounded-none border-[#E63946] text-[#E63946] hover:bg-[#FFEBEB] h-11 px-5 text-xs uppercase tracking-widest"
              data-testid="ann-delete-btn"
            >
              <Trash2 size={14} className="mr-2" /> Eliminar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
